/**
 * The top bar's Phase and Milestone choices, in planned order.
 *
 * Phases follow their configured order links and milestones follow theirs (blocking and order only
 * alike), each by depth in that order: something planned after another always lists below it. Items
 * with no order between them fall back to name order, which is the only order the choices had before.
 */
import type { UiMilestone, UiPhase } from '@nightshift/ui-types';
import { milestoneRanks } from '@nightshift/milestone-order';

const collator = new Intl.Collator('en', { numeric: true, sensitivity: 'base' });

/** Phases with every phase listed after the phases it is planned after. */
export function orderPhases(phases: readonly UiPhase[]): UiPhase[] {
  const ranks = milestoneRanks(phases.map((phase) => phase.id), new Map(phases.map((phase) => [phase.id, phase.orderAfter])));
  return [...phases].sort((a, b) => ranks.get(a.id)! - ranks.get(b.id)! || collator.compare(a.name, b.name) || a.id - b.id);
}

/** One phase's milestones, or the milestones with no phase when `phase` is null. */
export interface MilestoneChoiceGroup {
  phase: string | null;
  milestones: UiMilestone[];
}

/**
 * The Milestone choices, grouped by phase in phase order, milestones without a phase last.
 *
 * With `phaseId` set only that phase's group is returned, so the list holds its milestones alone.
 * A phase id that is not in `phases` matches no milestone.
 */
export function milestoneChoices(
  milestones: readonly UiMilestone[],
  phases: readonly UiPhase[],
  phaseId: number | null,
): MilestoneChoiceGroup[] {
  const ranks = milestoneRanks(
    milestones.map((milestone) => milestone.id),
    new Map(milestones.map((milestone) => [milestone.id, [...milestone.after, ...milestone.orderAfter].map((earlier) => earlier.id)])),
  );
  const sorted = [...milestones].sort((a, b) => ranks.get(a.id)! - ranks.get(b.id)! || collator.compare(a.name, b.name) || a.id - b.id);
  if (phaseId !== null) {
    const selected = phases.find((phase) => phase.id === phaseId);
    return [{ phase: selected?.name ?? null, milestones: selected ? sorted.filter((milestone) => milestone.phase === selected.name) : [] }];
  }
  const ordered: Array<string | null> = orderPhases(phases).map((phase) => phase.name);
  // A phase name the registry has not caught up with still gets a group rather than disappearing.
  for (const milestone of sorted) if (milestone.phase && !ordered.includes(milestone.phase)) ordered.push(milestone.phase);
  ordered.push(null);
  return ordered
    .map((phase) => ({ phase, milestones: sorted.filter((milestone) => (milestone.phase || null) === phase) }))
    .filter((group) => group.milestones.length > 0);
}

/** Progress shown in the top-bar phase and milestone choices. */
export function phaseProgress(phase: UiPhase, milestones: readonly UiMilestone[]): { completed: number; total: number } {
  const members = milestones.filter((milestone) => milestone.phase === phase.name);
  const total = phase.milestoneCount;
  const completed = members.filter((milestone) => milestone.ticketCount > 0 && milestone.openCount === 0).length;
  return { completed, total };
}
